declare global {
    interface Window {
        __ENV__?: { 
            REACT_APP_API_BASE_URL?: string;
            REACT_APP_TENANT_HOST?: string;
            REACT_APP_TENANT_SUBDOMAIN?: string;
            APP_ENV?: string;
            REACT_APP_APP_MODE?: string;
            REACT_APP_TURNSTILE_SITE_KEY?: string;
            REACT_APP_SERVER_TAG_SUGGESTIONS?: string;
            REACT_APP_VAPID_PUBLIC_KEY?: string;
        };
    }
}

const getRuntimeApiBaseUrl = (): string | undefined => {
    if (typeof window === 'undefined') return undefined;
    return window.__ENV__?.REACT_APP_API_BASE_URL;
};

const getRuntimeAppEnv = (): string | undefined => {
    if (typeof window === 'undefined') return undefined;
    return window.__ENV__?.APP_ENV;
};

const getRuntimeServerTagSuggestions = (): string | undefined => {
    if (typeof window === 'undefined') return undefined;
    return window.__ENV__?.REACT_APP_SERVER_TAG_SUGGESTIONS;
};

const getRuntimeVapidPublicKey = (): string | undefined => {
    if (typeof window === 'undefined') return undefined;
    return window.__ENV__?.REACT_APP_VAPID_PUBLIC_KEY;
};

/** Only `"1"` and `"true"` enable the feature (runtime env.js or build-time CRA inlining). */
const isServerTagSuggestionsEnabled = (value: string | undefined): boolean =>
    value === '1' || value === 'true';

function isLoopbackHost(hostname: string): boolean {
    return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1' || hostname === '[::1]';
}

/**
 * CRA's dev server proxies `/api` to safework-api. An absolute loopback base
 * (`http://localhost:7071`) makes the browser call that port on the machine
 * where the tab is open. Through a Cursor/SSH port forward that machine is
 * not the API host, so every call fails and Settings stays on Loading.
 * Same-origin requests stay on the forwarded dev server and hit the proxy.
 */
export function resolveDevApiBaseUrl(
    configured: string,
    page: { protocol: string; hostname: string; port: string } | null,
    nodeEnv: string | undefined
): string {
    if (nodeEnv !== 'development' || !page || !configured) {
        return configured;
    }
    let api: URL;
    try {
        api = new URL(configured);
    } catch {
        return configured;
    }
    if (!isLoopbackHost(api.hostname) || !isLoopbackHost(page.hostname)) {
        return configured;
    }
    const pagePort = page.port || (page.protocol === 'https:' ? '443' : '80');
    const apiPort = api.port || (api.protocol === 'https:' ? '443' : '80');
    if (api.protocol === page.protocol && api.hostname === page.hostname && apiPort === pagePort) {
        return configured;
    }
    return '';
}

const API_BASE_URL = resolveDevApiBaseUrl(
    getRuntimeApiBaseUrl() || process.env.REACT_APP_API_BASE_URL || '',
    typeof window === 'undefined' ? null : window.location,
    process.env.NODE_ENV
);
export const APP_ENV = getRuntimeAppEnv() || process.env.REACT_APP_APP_ENV || process.env.APP_ENV || 'production';

/** Deployment experience: 'community' (default) or 'b2b' (analytics-only dashboard). */
export const APP_MODE =
  (typeof window !== 'undefined' && window.__ENV__?.REACT_APP_APP_MODE) ||
  process.env.REACT_APP_APP_MODE ||
  'community';
export const IS_B2B = APP_MODE === 'b2b';

/**
 * Desktop shell (Tauri v2). Windows serves the SPA from http://tauri.localhost;
 * the internals globals are set on every Tauri webview.
 */
export const IS_TAURI =
  typeof window !== 'undefined' &&
  ('__TAURI_INTERNALS__' in window ||
    '__TAURI__' in window ||
    window.location.hostname === 'tauri.localhost');

const readNonEmpty = (value: string | undefined): string | undefined => {
  if (!value) return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const getRuntimeTenantHost = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  return readNonEmpty(window.__ENV__?.REACT_APP_TENANT_HOST);
};

function extractHostname(hostOrUrl: string): string {
  const trimmed = hostOrUrl.trim();
  if (!trimmed) return '';
  try {
    const url = trimmed.includes('://') ? new URL(trimmed) : new URL(`http://${trimmed}`);
    return url.hostname.toLowerCase();
  } catch {
    return trimmed.split('/')[0].split(':')[0].toLowerCase();
  }
}

/**
 * Non-loopback host of the configured API base URL, including a non-default port.
 * Loopback API URLs (e.g. `http://localhost:7071` in local dev) must not be
 * treated as a remote tenant host.
 */
export function tenantHostFromApiBaseUrl(
  configuredApiBaseUrl: string | undefined =
    readNonEmpty(getRuntimeApiBaseUrl()) || readNonEmpty(process.env.REACT_APP_API_BASE_URL)
): string | undefined {
  const configured = readNonEmpty(configuredApiBaseUrl);
  if (!configured) return undefined;
  try {
    const parsed = new URL(configured);
    if (isLoopbackHost(parsed.hostname.toLowerCase())) {
      return undefined;
    }
    return readNonEmpty(parsed.host);
  } catch {
    return undefined;
  }
}

/**
 * Explicit non-loopback tenant host, or (Tauri only) the non-loopback host of
 * the configured API base URL. Undefined means the page host is the tenant.
 */
export function resolveConfiguredTenantHost(
  explicitTenantHost: string | undefined =
    getRuntimeTenantHost() || readNonEmpty(process.env.REACT_APP_TENANT_HOST),
  configuredApiBaseUrl: string | undefined =
    readNonEmpty(getRuntimeApiBaseUrl()) || readNonEmpty(process.env.REACT_APP_API_BASE_URL),
  isTauri: boolean = IS_TAURI
): string | undefined {
  const explicit = readNonEmpty(explicitTenantHost);
  if (explicit && !isLoopbackHost(extractHostname(explicit))) {
    return explicit;
  }
  if (isTauri) {
    return tenantHostFromApiBaseUrl(configuredApiBaseUrl);
  }
  return undefined;
}

function configuredTenantHost(): string | undefined {
  return resolveConfiguredTenantHost();
}

/** Tenant host used for routing headers and cache keys. */
export function getEffectiveTenantHost(): string {
  const configured = configuredTenantHost();
  if (configured) return configured;
  if (typeof window === 'undefined') return '';
  return window.location.host;
}

/** Origin registered with the IdP (`https://<tenant-host>` when configured). */
export function getEffectiveTenantOrigin(): string {
  const configured = configuredTenantHost();
  if (configured) return `https://${configured}`;
  if (typeof window === 'undefined') return '';
  return window.location.origin;
}

/** Effective tenant host for this page load (configured host, else the page host). */
export const TENANT_HOST = getEffectiveTenantHost();

/** Post-login / fallback landing route for the current experience. */
export const HOME_ROUTE = IS_B2B ? '/app/communitydashboard' : '/app/community';

/** Video pipeline: opt out with REACT_APP_VIDEO_DASH_ENABLED=0 */
export const FEATURE_VIDEO_DASH_PLAYBACK =
  process.env.REACT_APP_VIDEO_DASH_ENABLED !== '0' && process.env.REACT_APP_VIDEO_DASH_ENABLED !== 'false';

/** Structured media[] from API alongside image_data; opt out with REACT_APP_USE_STRUCTURED_MEDIA=0 */
export const FEATURE_STRUCTURED_MEDIA =
  process.env.REACT_APP_USE_STRUCTURED_MEDIA !== '0' && process.env.REACT_APP_USE_STRUCTURED_MEDIA !== 'false';

/** Raw build-time value for diagnostics (Create React App inlines only vars present at compile time). */
export const REACT_APP_SERVER_TAG_SUGGESTIONS_RAW =
  process.env.REACT_APP_SERVER_TAG_SUGGESTIONS ?? '';

/** Raw runtime value from `env.js` / `window.__ENV__` (empty if unset or non-browser). */
export const REACT_APP_SERVER_TAG_SUGGESTIONS_RUNTIME_RAW =
  getRuntimeServerTagSuggestions() ?? '';

/** When true, Submit/Edit hazard UIs call POST /api/classifyHazardTags for tag suggestions (requires online). */
export const FEATURE_SERVER_TAG_SUGGESTIONS =
  isServerTagSuggestionsEnabled(REACT_APP_SERVER_TAG_SUGGESTIONS_RUNTIME_RAW) ||
  isServerTagSuggestionsEnabled(REACT_APP_SERVER_TAG_SUGGESTIONS_RAW);

/** Web Push VAPID public key (runtime env.js takes precedence over build-time CRA inlining). */
export const VAPID_PUBLIC_KEY = getRuntimeVapidPublicKey() || process.env.REACT_APP_VAPID_PUBLIC_KEY || '';

export default API_BASE_URL;
